// src/utils/sowParser.js
// Parses uploaded Scope of Work files (PDF, DOCX, XLSX, CSV) and groups
// the verbatim content under the closest matching template stage so the
// import wizard can show a side-by-side review.
//
// Design principles:
// - Content from the uploaded document is NEVER rewritten or paraphrased.
//   Only stable structural decisions (which line is a heading, which line
//   is a list item, which stage it belongs to) are inferred. The wizard
//   surfaces every decision the parser made for the user to override.
// - The template is used purely as a STRUCTURAL ANCHOR — its stage names
//   and aliases tell us how to bucket detected sections. No template
//   wording ever ends up in the imported scope.
// - List items, not paragraphs, become deliverables. Both glyph bullets
//   (•, -, —, *, …) and numbered items (1., 2., (1), (2)) qualify, but
//   stage headings that happen to start with a number ("1. Concept Design
//   Phase") take precedence over the list-item classification.

import { DEFAULT_SCOPE_TEMPLATE } from './contractScopeHelpers';

// ============================================================================
// FILE READING
// ============================================================================

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file as text'));
    reader.readAsText(file);
  });
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// ============================================================================
// LINE EXTRACTION BY FILE TYPE
// ============================================================================

async function extractLinesFromCSV(file) {
  const text = await readFileAsText(file);
  return text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
}

async function extractLinesFromExcel(file) {
  const ExcelJS = (await import('exceljs')).default;
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const lines = [];
  workbook.eachSheet((sheet) => {
    sheet.eachRow((row) => {
      const cells = [];
      row.eachCell({ includeEmpty: false }, (cell) => {
        const val = cell.text || String(cell.value || '');
        if (val.trim()) cells.push(val.trim());
      });
      if (cells.length > 0) lines.push(cells.join(' | '));
    });
  });
  return lines;
}

// PDF: group items by y-coordinate per page so visually-aligned text
// stays on one logical line. Bullet/heading rows are short and lose
// formatting in pdfjs's flat item stream — y-grouping recovers them.
async function extractLinesFromPDF(file) {
  const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
  const pdfjsWorker = await import('pdfjs-dist/legacy/build/pdf.worker.entry');
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const pdfDoc = await pdfjsLib.getDocument({
    data: new Uint8Array(await readFileAsArrayBuffer(file)),
  }).promise;

  const lines = [];
  for (let p = 1; p <= pdfDoc.numPages; p++) {
    const page = await pdfDoc.getPage(p);
    const content = await page.getTextContent();
    const rows = new Map();
    for (const item of content.items) {
      if (!item.str || !item.str.trim()) continue;
      const y = Math.round((item.transform?.[5] ?? 0) * 10) / 10;
      const x = item.transform?.[4] ?? 0;
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push({ x, str: item.str });
    }
    const sortedYs = [...rows.keys()].sort((a, b) => b - a);
    for (const y of sortedYs) {
      const row = rows.get(y).sort((a, b) => a.x - b.x);
      const text = row.map(r => r.str).join(' ').replace(/\s+/g, ' ').trim();
      if (text) lines.push(text);
    }
  }
  return lines;
}

// DOCX: use mammoth to convert to HTML, then walk the DOM. Mammoth
// renders Word auto-numbered lists as <ol><li>…</li></ol> WITHOUT the
// numbers in the text content (the browser renders them from the <ol>).
// To stay verbatim with the source document we re-attach the numbers
// from the <li> index. Bullet lists (<ul>) get a "• " prefix so the
// downstream classifier treats them as bullets.
//
// We deliberately DON'T try to support legacy .doc (Word 97-2003 binary
// OLE format) — that requires a very heavy parser and is rare today.
// We surface a clear error and ask the user to re-save as .docx.
async function extractLinesFromDocx(file) {
  // mammoth 1.x ships node bits at lib/index.js with browser overrides
  // (see its package.json "browser" map) — webpack picks the right
  // versions of unzip/files automatically. Dynamic import keeps the
  // ~200KB library out of the main bundle until a .docx is uploaded.
  const mammothMod = await import('mammoth');
  const mammoth = mammothMod.default || mammothMod;
  const buffer = await readFileAsArrayBuffer(file);
  const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
  return extractLinesFromHtml(result.value || '');
}

// Public utility: convert an HTML fragment into the same line array the
// rest of the parser expects. Used by .docx import (after mammoth
// converts the document) and by paste-from-clipboard (where the
// browser hands us the source app's HTML clipboard representation
// directly — particularly useful for Apple Pages docs, whose .docx
// export drops list-numbering metadata that the clipboard preserves).
//
// Walker rules:
// - <ol>: rebuild the numbering from the <li> index, prefixing each
//   line with "1. ", "2. ", … honouring an `<ol start="N">` seed when
//   present (Word emits this when a numbered list spans page breaks).
// - <ul>: prefix each <li> with "• " so the downstream classifier
//   treats it as a bullet.
// - Standalone <li> with no list parent (rare): falls back to "• ".
// - <p>, <div>, <h1..h6>: emit textContent as a plain line.
// - <br>: split the surrounding paragraph at the line break.
export function extractLinesFromHtml(html) {
  if (!html || typeof html !== 'string') return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const lines = [];

  function pushLi(li, marker) {
    const text = (li.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) lines.push(`${marker}${text}`);
  }

  function pushBlockText(node) {
    // Honour <br> by splitting the block into multiple lines —
    // pasted Pages content sometimes uses <br> inside a paragraph
    // for soft line breaks within a single deliverable's text.
    const html = node.innerHTML || '';
    const segments = html.split(/<br\s*\/?>(?![^<]*<\/(?:script|style)>)/i);
    for (const seg of segments) {
      const tmp = doc.createElement('div');
      tmp.innerHTML = seg;
      const text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) lines.push(text);
    }
  }

  function walk(node) {
    if (!node || node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();

    if (tag === 'ol' || tag === 'ul') {
      const isOrdered = tag === 'ol';
      const startAttr = parseInt(node.getAttribute('start') || '', 10);
      let counter = Number.isFinite(startAttr) && startAttr > 0 ? startAttr : 1;
      for (const child of node.children) {
        const childTag = child.tagName.toLowerCase();
        if (childTag === 'li') {
          pushLi(child, isOrdered ? `${counter}. ` : '• ');
          counter += 1;
        } else {
          walk(child);
        }
      }
      return;
    }

    if (tag === 'li') {
      pushLi(node, '• ');
      return;
    }

    if (tag === 'p' || /^h[1-6]$/.test(tag) || tag === 'div') {
      pushBlockText(node);
      return;
    }

    // Wrappers (body, table cells, etc.) — recurse into element children.
    for (const child of node.children) walk(child);
  }

  walk(doc.body);
  return lines;
}

// ============================================================================
// LIST ITEM / HEADING REGEXES
// ============================================================================

// Glyph-style bullets only.
const BULLET_GLYPH_RE = /^\s*[•‣◦⁃∙▪◆►●·⋅\-–—*]\s+/;
// Numbered list items: "1.", "2.", "(1)", "(2)", "1)", "a)"…
const NUMBERED_RE = /^\s*(\d+[.)]|\(\d+\)|[a-z][.)])\s+/i;
// A line is a list item if it matches either flavour.
const LIST_ITEM_RE = new RegExp(
  '^\\s*(' +
    '[•‣◦⁃∙▪◆►●·⋅\\-–—*]' +     // glyph bullet
    '|\\d+[.)]' +                  // 1. / 1)
    '|\\(\\d+\\)' +                // (1)
    '|[a-z][.)]' +                 // a. / a)
  ')\\s+',
  'i',
);

function isListItem(line) {
  if (!line) return false;
  const t = line.trim();
  if (t.length < 3 || t.length > 400) return false;
  return LIST_ITEM_RE.test(t);
}

function stripListMarker(line) {
  return line.replace(LIST_ITEM_RE, '').trim();
}

// Wrap-merge: PDF extraction (and sometimes DOCX) splits a single
// logical list item across two lines when it overflows. We re-glue
// continuation lines into the preceding list item under either of two
// signals: the continuation starts with a clearly mid-clause character
// (lowercase letter, digit, paren) OR the previous line ended with a
// connector word ("with", "and", "the", "to", "of", …). Content stays
// verbatim — only the line break introduced by the layout engine is
// removed.
const CONNECTOR_END_RE = /\b(a|an|and|as|at|but|by|for|from|in|including|into|of|on|or|over|the|their|to|under|until|while|with|its|our|that|this|these|those|after|before|between|among|through|during)$/i;

function mergeListContinuations(lines) {
  const merged = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (merged.length === 0) { merged.push(line); continue; }
    const lastIdx = merged.length - 1;
    const lastTrimmed = merged[lastIdx].trim();
    const lastIsListItem = LIST_ITEM_RE.test(lastTrimmed);
    const currentIsListItem = LIST_ITEM_RE.test(trimmed);
    const startsContinuation = /^[a-z(/0-9)]/.test(trimmed);
    const prevEndsMidClause = CONNECTOR_END_RE.test(lastTrimmed);
    if (
      lastIsListItem &&
      !currentIsListItem &&
      (startsContinuation || prevEndsMidClause) &&
      trimmed.length < 200
    ) {
      merged[lastIdx] = `${merged[lastIdx]} ${trimmed}`.replace(/\s+/g, ' ');
    } else {
      merged.push(line);
    }
  }
  return merged;
}

// ============================================================================
// HEADING DETECTION
// ============================================================================

function normalise(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Words that show up in PDF stage headings as scaffolding ("Phase X",
// "Stage Y") rather than as part of the stage name itself. We strip
// them before scoring so "Phase Schematic Design" can match
// "Schematic Design".
const HEADING_STOP_WORDS = new Set([
  'phase', 'stage', 'section', 'chapter', 'and', 'the', 'of', 'for', 'a', 'an',
]);

function meaningfulWords(text) {
  // Length > 2 drops short connector words ("on", "in", "to", "by")
  // that produced false substring matches like "on" ⊂ "completion".
  return normalise(text).split(' ').filter(w => w.length > 2 && !HEADING_STOP_WORDS.has(w));
}

function isLikelyHeading(line) {
  if (!line) return false;
  const t = line.trim();
  if (t.length === 0 || t.length > 90) return false;
  // Glyph bullets (•, -, *) are content, never headings. We DO allow
  // numbered prefixes ("1. Concept Design Phase" is a heading).
  if (BULLET_GLYPH_RE.test(t)) return false;
  // Lines ending in ":" are typically intro markers (e.g.
  // "Deliverables will be as follows:", "Tender Drawings shall include,
  // but not be limited to:"), not headings.
  if (t.endsWith(':')) return false;
  // Reject prose-y multi-clause lines (more than 2 sentence punctuation
  // marks usually means an actual sentence, not a heading).
  const sentenceMarkers = (t.match(/[.;]/g) || []).length;
  if (sentenceMarkers > 2) return false;
  return true;
}

function stageLabelsFor(stageTpl) {
  const labels = new Set();
  if (stageTpl.name) labels.add(stageTpl.name);
  if (stageTpl.fullName) {
    labels.add(stageTpl.fullName);
    // Split on em/en-dash so "Concept Design — Architecture" also exposes
    // "Concept Design" — the part PDFs typically use as a heading without
    // the discipline tag.
    const parts = stageTpl.fullName.split(/\s*[—–-]\s*/);
    if (parts.length > 1) parts.forEach(p => p.trim() && labels.add(p.trim()));
  }
  if (stageTpl.key) labels.add(String(stageTpl.key).replace(/-/g, ' '));
  return [...labels].filter(Boolean);
}

function detectStageOnLine(line, template, discipline) {
  if (!isLikelyHeading(line)) return null;
  // Strip a leading numeric / bullet marker so "1. Concept Design Phase"
  // is scored on "Concept Design Phase".
  const stripped = stripListMarker(line.trim());
  const nLine = normalise(stripped);
  if (!nLine) return null;
  const lineWords = meaningfulWords(stripped);
  if (lineWords.length === 0) return null;

  // Discipline bonus: a small score boost for stages whose key suffix
  // matches the document-level discipline. This breaks ties between
  // shared primary labels ("Concept Design" lives on both cd-ad and
  // cd-id) so a pure Interior SOW maps generic "Concept Design Phase"
  // headings to cd-id rather than cd-ad. Combined SOWs leave
  // discipline === undefined and the existing AD-first ordering wins.
  const disciplineBonus = (stageKey) => {
    if (!discipline || !stageKey) return 0;
    if (discipline === 'interior' && stageKey.endsWith('-id')) return 0.02;
    if (discipline === 'architecture' && stageKey.endsWith('-ad')) return 0.02;
    return 0;
  };

  let best = null;
  for (const stage of template.stages) {
    for (const label of stageLabelsFor(stage)) {
      const nLabel = normalise(label);
      if (!nLabel) continue;
      let score = 0;
      if (nLine === nLabel) score = 1.0;
      // Density 0.6 keeps "Concept Design Phase" matching the
      // "Concept Design" primary half (density 0.63) but stops
      // "Interior Schematic Design Phase" from collapsing onto the
      // shared "Schematic Design" primary (density 0.5) — instead
      // word-overlap correctly picks sd-id over sd-ad.
      else if (nLine.includes(nLabel) && nLabel.length / nLine.length >= 0.6) score = 0.9;
      else if (nLabel.includes(nLine) && nLine.length / nLabel.length >= 0.6) score = 0.85;
      else {
        const labelWords = meaningfulWords(label);
        if (labelWords.length === 0) continue;
        const lineSet = new Set(lineWords);
        let inter = 0;
        for (const lw of labelWords) {
          if (lineSet.has(lw)) {
            inter++;
          } else if (lw.length >= 5 && [...lineSet].some(w => w.includes(lw) || lw.includes(w))) {
            inter++;
          }
        }
        const labelCov = inter / labelWords.length;
        const lineCov = inter / lineWords.length;
        if (labelCov >= 0.4 && lineCov >= 0.5) {
          score = 0.6 + 0.1 * labelCov + 0.1 * lineCov; // 0.70..0.80
        }
      }
      const adjusted = score > 0 ? score + disciplineBonus(stage.key) : 0;
      if (adjusted >= 0.7 && (!best || adjusted > best.score)) {
        best = { stageKey: stage.key, score: adjusted, via: 'label' };
      }
    }

    // Acronym fallback: many BRIO stage codes are 3-6 character
    // acronyms (GFC, PCD, AoC). If the line contains the stage's
    // acronym as a whole word, accept with a moderate score (0.72).
    // This catches headings like "Building GFC Set R 0" where the
    // PDF doesn't echo the template's full stage name. Lower-bound
    // length 3 keeps it from triggering on common 2-letter codes
    // (CD, AD, ID, CC) that would over-match.
    const firstToken = (stage.name || '').trim().split(/\s+/)[0] || '';
    const looksLikeAcronym = firstToken.length >= 3 && firstToken.length <= 6
      && (firstToken.match(/[A-Z]/g) || []).length >= 2;
    if (looksLikeAcronym) {
      const escaped = firstToken.replace(/[^A-Za-z0-9]/g, '');
      if (escaped) {
        const tokenRe = new RegExp(`\\b${escaped}\\b`, 'i');
        if (tokenRe.test(line)) {
          const acronymScore = 0.72 + disciplineBonus(stage.key);
          if (!best || acronymScore > best.score) {
            best = { stageKey: stage.key, score: acronymScore, via: 'acronym' };
          }
        }
      }
    }
  }
  return best;
}

// Detect the document's overall discipline (Architecture / Interior /
// combined) from the first ~12 lines (the title region of typical
// SOWs). Used to bias stage matching when the heading itself is
// ambiguous: a pure Interior SOW says "Concept Design Phase" without
// an "Interior" qualifier, but the title says "Scope of Work -
// Interior Design" — that signal flows down to detectStageOnLine.
function detectDocumentDiscipline(lines) {
  const head = (lines || []).slice(0, 12).join(' ').toLowerCase();
  const hasInterior = /\binterior(\s+design)?\b/.test(head);
  const hasArchitecture = /\b(architecture|architectural|building)\b/.test(head);
  if (hasInterior && hasArchitecture) return undefined; // combined SOW
  if (hasInterior) return 'interior';
  if (hasArchitecture) return 'architecture';
  return undefined;
}

// ============================================================================
// INTERIOR-DELIVERABLES SECTION DETECTION
// ============================================================================
// PDFs sometimes carry a dedicated "INTERIOR DESIGN" / "Scope of work
// Interior Design" block that lists the project's interior
// responsibilities. In the BRIO template these aren't a stage — they
// live on the scope's top-level interiorDeliverables array. We detect
// that section before stage matching so the heading doesn't get fuzzed
// onto a "Concept Design — Interior" stage via word overlap on
// "interior".

function isInteriorDeliverablesHeading(line, discipline) {
  if (!line) return false;
  const t = line.trim();
  if (t.length === 0 || t.length > 90) return false;
  if (BULLET_GLYPH_RE.test(t)) return false;
  if (t.endsWith(':')) return false;
  const sentenceMarkers = (t.match(/[.;]/g) || []).length;
  if (sentenceMarkers > 2) return false;

  const n = normalise(line).replace(/^\d+\s+/, '').trim();
  if (!n) return false;
  if (n === 'interior design') return true;
  if (/^scope\s+of\s+work\s+interior(\s+design)?$/.test(n)) return true;
  if (/^interior\s+design\s+scope(\s+of\s+work)?$/.test(n)) return true;
  // In a pure Interior SOW the deliverables block may be titled just
  // "Summary of Deliverables" (with no "Interior" qualifier). Accept
  // that variant only when the document discipline is interior so we
  // don't latch onto generic summary headings in combined SOWs.
  if (discipline === 'interior') {
    if (/^summary\s+of\s+(interior\s+)?deliverables$/.test(n)) return true;
  }
  return false;
}

// ============================================================================
// SUB-HEADER DETECTION (within a stage's prose)
// ============================================================================
// SOW PDFs typically structure each stage's narrative with short
// title-cased sub-headings like "Programming", "Pre-Concept Design",
// "Final Concept Design". We detect those so the formatted introduction
// wraps them in **bold** markup — the same convention the BRIO standard
// template's introduction text uses.

// Words that strongly indicate a full sentence rather than a header
// (verbs in the imperative or present tense common in scope-of-work
// narrative).
const SENTENCE_VERB_RE = /\b(shall|will|must|should|may|provide|prepare|develop|include|review|conduct|ensure|allow|require|describe|outline|assist|involves|engage|present|support|coordinate|approved|incorporate|attend|intended|appointed|takes|gets|appears|exists|consists|capture|specify|enable|distribute|recommend|select|carry|serve|exceed|guide|note|account|request|address|manage)\b/i;

function isLikelySubHeader(text) {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 3 || t.length > 60) return false;
  if (/[.;:,]$/.test(t)) return false;
  if (BULLET_GLYPH_RE.test(t)) return false;
  if (/^\d+[.)]/.test(t)) return false;
  if (SENTENCE_VERB_RE.test(t)) return false;
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 6) return false;
  // Most words start with an uppercase letter (Title Case heuristic).
  const upper = words.filter(w => /^[A-Z]/.test(w)).length;
  return upper / words.length >= 0.7;
}

function formatProseAsIntro(proseTexts) {
  if (!proseTexts || proseTexts.length === 0) return '';
  // Wrap likely sub-headers in **bold** with a leading blank line so
  // the rendered scope picks up paragraph spacing similarly to the
  // standard template's introduction text.
  const out = [];
  for (let i = 0; i < proseTexts.length; i++) {
    const t = proseTexts[i];
    if (isLikelySubHeader(t)) {
      // Blank line before the header (unless we're at the top).
      if (out.length > 0) out.push('');
      out.push(`**${t}**`);
    } else {
      out.push(t);
    }
  }
  return out.join('\n').trim();
}

// ============================================================================
// MAIN PARSER
// ============================================================================

const SECTION_PREAMBLE = -1;
const SECTION_INTERIOR = -2;

function parseAgainstTemplate(rawLines, template) {
  const lines = mergeListContinuations(rawLines);

  // Determine the document's overall discipline up front (Interior /
  // Architecture / combined). Used to bias generic stage headings
  // ("Concept Design Phase") toward the right variant.
  const discipline = detectDocumentDiscipline(lines);

  // Section index per logical line.
  const sectionByLine = new Array(lines.length).fill(SECTION_PREAMBLE);
  // Lines flagged as section headings are skipped from the content
  // buckets so they don't leak into the saved scope.
  const isHeadingLine = new Array(lines.length).fill(false);

  let current = SECTION_PREAMBLE;
  // Stages we've already entered. Used to advance acronym-matched
  // repeats to the next unclaimed sibling: HB SOWs split GFC into
  // "Building GFC Set R 0" and "Interior Design GFC Set R 0", and we
  // want them mapped to gfc-1 and gfc-2 respectively rather than
  // collapsing the second one back onto gfc-1.
  const consumed = new Set();

  for (let i = 0; i < lines.length; i++) {
    if (isInteriorDeliverablesHeading(lines[i], discipline)) {
      current = SECTION_INTERIOR;
      isHeadingLine[i] = true;
      sectionByLine[i] = current;
      continue;
    }
    const hit = detectStageOnLine(lines[i], template, discipline);
    if (hit) {
      let idx = template.stages.findIndex(s => s.key === hit.stageKey);
      // Acronym repeats: when an acronym hit lands on a stage we've
      // already entered, look for the next unclaimed template stage
      // sharing the same first-token acronym. If none is available,
      // fall through to the same-stage skip below.
      if (idx !== -1 && hit.via === 'acronym' && consumed.has(idx)) {
        const acronym = (template.stages[idx].name || '').trim().split(/\s+/)[0];
        const nextIdx = template.stages.findIndex((s, k) => {
          if (consumed.has(k)) return false;
          const t = (s.name || '').trim().split(/\s+/)[0];
          return t === acronym;
        });
        if (nextIdx !== -1) idx = nextIdx;
        else idx = -1; // exhausted siblings — keep current stage
      }
      // Only flag as a heading line when it actually changes section.
      // A redundant re-detection of the current stage (a sub-heading
      // like "Concept Design" inside the Concept Design phase, or a
      // prose fragment that happens to score against the current
      // stage's labels) shouldn't be removed from the bucket.
      if (idx !== -1 && idx !== current) {
        current = idx;
        consumed.add(idx);
        isHeadingLine[i] = true;
      }
    }
    sectionByLine[i] = current;
  }

  // Each bucket keeps items in document order with a kind tag so we
  // can split prose into "introduction" (everything up to and including
  // the last bullet's surroundings) vs "notes" (prose AFTER the last
  // bullet, before the next stage heading).
  const stageBuckets = template.stages.map(() => ({ items: [] }));
  const interiorBucket = { items: [] };
  const preambleBullets = [];
  const preambleProse = [];

  for (let i = 0; i < lines.length; i++) {
    if (isHeadingLine[i]) continue;
    const text = lines[i];
    const idx = sectionByLine[i];
    let target;
    if (idx === SECTION_INTERIOR) target = interiorBucket;
    else if (idx === SECTION_PREAMBLE) target = null;
    else target = stageBuckets[idx];

    const isBullet = isListItem(text);
    const item = { text, kind: isBullet ? 'bullet' : 'prose', index: i, originalText: text };
    if (target) {
      target.items.push(item);
    } else if (isBullet) {
      preambleBullets.push(item);
    } else {
      preambleProse.push(item);
    }
  }

  const stageResults = template.stages.map((stageTpl, idx) => {
    const items = stageBuckets[idx].items;
    // Position of the last bullet in document order — prose after it
    // becomes the stage's "notes", prose before/around becomes the
    // "introduction" draft.
    let lastBulletPos = -1;
    for (let k = items.length - 1; k >= 0; k--) {
      if (items[k].kind === 'bullet') { lastBulletPos = k; break; }
    }
    const introItems = lastBulletPos === -1 ? items : items.slice(0, lastBulletPos + 1);
    const notesItems = lastBulletPos === -1 ? [] : items.slice(lastBulletPos + 1);

    const bullets = items.filter(it => it.kind === 'bullet');
    const introProse = introItems.filter(it => it.kind === 'prose').map(it => it.text);
    const notesProse = notesItems.map(it => it.text); // already prose by construction

    return {
      key: stageTpl.key,
      stageId: stageTpl.stageId,
      name: stageTpl.name,
      fullName: stageTpl.fullName || stageTpl.name,
      sectionDetected: items.length > 0,
      bulletLines: bullets.map(b => ({
        // Strip leading list marker (•, 1., etc.) from the displayed
        // label. Original line is kept on `originalText` for traceability.
        text: stripListMarker(b.text),
        originalText: b.text,
        index: b.index,
      })),
      // Prose lines stay available for callers that want them raw, but
      // the wizard reads introductionDraft / notesDraft directly.
      proseLines: introItems.filter(it => it.kind === 'prose').map(p => ({
        text: p.text,
        originalText: p.text,
        index: p.index,
      })),
      introductionDraft: formatProseAsIntro(introProse),
      notesDraft: notesProse.join('\n').trim(),
    };
  });

  const interiorBullets = interiorBucket.items
    .filter(it => it.kind === 'bullet')
    .map(b => ({ text: stripListMarker(b.text), originalText: b.text, index: b.index }));
  const interiorProse = interiorBucket.items
    .filter(it => it.kind === 'prose')
    .map(p => ({ text: p.text, originalText: p.text, index: p.index }));
  const interiorDetected = interiorBucket.items.length > 0;

  const unassignedBullets = preambleBullets.map(b => ({
    text: stripListMarker(b.text),
    originalText: b.text,
    index: b.index,
  }));
  const unassignedProse = preambleProse.map(p => ({
    text: p.text,
    originalText: p.text,
    index: p.index,
  }));

  return {
    lines,
    discipline,
    stages: stageResults,
    interiorDetected,
    interiorBullets,
    interiorProse,
    unassignedBullets,
    unassignedProse,
    sectionByLine,
  };
}

// ============================================================================
// PUBLIC API
// ============================================================================

const SUPPORTED_MIME = {
  'text/csv': 'csv',
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc-legacy',
};
const SUPPORTED_EXT = {
  csv: 'csv', xls: 'excel', xlsx: 'excel',
  pdf: 'pdf', docx: 'docx', doc: 'doc-legacy',
  pages: 'pages-unsupported',
};

export function getFileType(file) {
  if (SUPPORTED_MIME[file.type]) return SUPPORTED_MIME[file.type];
  const ext = (file.name || '').split('.').pop().toLowerCase();
  return SUPPORTED_EXT[ext] || null;
}

export async function extractLines(file) {
  const fileType = getFileType(file);
  if (!fileType) {
    throw new Error('Unsupported file type. Upload a PDF, Word document (.docx), Excel (.xlsx/.xls), or CSV — or use the Paste-from-clipboard option below.');
  }
  if (fileType === 'doc-legacy') {
    throw new Error('Legacy .doc files (Word 97–2003) aren\'t supported. Please open the file in Word and save it as .docx, then upload that.');
  }
  if (fileType === 'pages-unsupported') {
    throw new Error('Apple Pages files can\'t be opened directly (the format is closed). For best results, copy the scope-of-work content from Pages and paste it into the "Paste from clipboard" section below — list numbering and bullets will be preserved.');
  }
  let lines;
  switch (fileType) {
    case 'csv': lines = await extractLinesFromCSV(file); break;
    case 'excel': lines = await extractLinesFromExcel(file); break;
    case 'pdf': lines = await extractLinesFromPDF(file); break;
    case 'docx': lines = await extractLinesFromDocx(file); break;
    default: throw new Error('Unsupported file type.');
  }
  if (!lines.length) throw new Error('No readable text found in the uploaded file.');
  return { fileType, lines };
}

export async function parseSOWFile(file, template = DEFAULT_SCOPE_TEMPLATE) {
  const { fileType, lines } = await extractLines(file);
  const matchResult = parseAgainstTemplate(lines, template);
  return {
    fileName: file.name,
    fileType,
    templateId: template.id,
    templateName: template.name,
    ...matchResult,
    totalLines: matchResult.lines.length,
  };
}

// Parse pasted-from-clipboard HTML (or plain text wrapped in HTML).
// `fileName` is just the title shown in the wizard — pass anything
// descriptive (e.g. "Pasted from Pages").
export function parseSOWFromHtml(html, fileName = 'Pasted content', template = DEFAULT_SCOPE_TEMPLATE) {
  const lines = extractLinesFromHtml(html);
  if (!lines.length) {
    throw new Error('No readable text in the pasted content. Make sure you copied the scope-of-work text first, then pasted it here.');
  }
  const matchResult = parseAgainstTemplate(lines, template);
  return {
    fileName,
    fileType: 'paste',
    templateId: template.id,
    templateName: template.name,
    ...matchResult,
    totalLines: matchResult.lines.length,
  };
}
